import React from 'react';
import { ArrowRight, Check, BarChart3, Heart, Activity, Brain } from 'lucide-react';
import Section, { SectionTitle, SectionSubtitle } from '../components/ui/Section';
import Card, { CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import TeamSection from '../components/TeamSection';
import HeroSection from '../components/ui/HeroSection';
import HeroCard from '../components/ui/HeroCard';
import CTASection from '../components/ui/CTASection';
import FeatureCard from '../components/ui/FeatureCard';
import ApplicationCard from '../components/ApplicationCard';


const HomePage = () => {
  return (
    <div id="top" className="font-sans text-black">
      {/* Hero Section */}
      <HeroSection
        title="Decoding the Language of the Body"
        description="Senti's Large Biosignal Model (LBM) powers the diagnostic medical devices of tomorrow. Together with our medical device partners, we're transforming how disease is treated through our AI that understands the body's signals."
        buttonText="Learn More"
        buttonLink="/technology"
        buttonVariant="primary"
        buttonScrollTo="technology"
        secondButtonText="For Partners"
        secondButtonLink="/partners"
        secondButtonScrollTo="partners"
        centered={false}
        paddingY="py-32"
      >
        <HeroCard 
          icon={<Activity className="h-20 w-20 text-primary" />}
          title="Proactive, Precise Healthcare"
          description="Our AI technology interprets complex biosignals to detect diseases earlier and more accurately than ever before."
        />
      </HeroSection>

      {/* Key Benefits Section */}
      <Section id="technology">
        <SectionTitle>The Future of Healthcare Diagnostics</SectionTitle>
        
        <div className="grid md:grid-cols-3 gap-10">
          <FeatureCard
            icon={<Brain className="h-8 w-8 text-primary" />}
            title="Large Biosignal Model"
            description="Our proprietary LBM technology interprets complex signals from the human body, extracting biomarkers with unmatched precision."
            variant="accent"
          />
          <FeatureCard
            icon={<BarChart3 className="h-8 w-8 text-primary" />}
            title="Early Disease Detection"
            description="Detect conditions at their earliest stages, enabling timely interventions that save lives and significantly reduce healthcare costs."
            variant="accent"
          />
          <FeatureCard
            icon={<Heart className="h-8 w-8 text-primary" />}
            title="Proactive Care Model"
            description="Transform healthcare from reactive to proactive, empowering patients and clinicians with real-time health insights."
            variant="accent"
          />
        </div>
      </Section>

      {/* Applications Section */}
      <Section id="applications" background="light">
        <SectionTitle>Real-World Applications</SectionTitle>
        <SectionSubtitle>
          Our LBM is already proving its effectiveness across multiple healthcare domains.
        </SectionSubtitle>
        
        <div className="grid md:grid-cols-3 gap-8">
          <ApplicationCard
            title="Respiratory Disease Management"
            description="Our respiratory wearable device monitors breath and heart sounds to detect early signs of flare-ups."
            accentColor="primary"
            bulletPoints={[
              "9Potential to save billions in healthcare costs and dramatically improve outcomes for 136M respiratory disease patients."
            ]}
            variant="simple"
          />
          
          <ApplicationCard
            title="Rheumatoid Arthritis Diagnosis"
            description="The Arthritect system accelerates the diagnosis of rheumatoid arthritis from months to weeks."
            accentColor="secondary"
            bulletPoints={[
              "Could unlock over £1.5 billion in direct healthcare savings annually through earlier detection and treatment."
            ]}
            variant="simple"
          />
          
          <ApplicationCard
            title="Fetal Wellbeing Monitoring"
            description="Bitbaby monitors fetal movements to provide reliable measures of fetal health."
            accentColor="accent"
            bulletPoints={[
              "Helping to reduce stillbirths among the 12M pregnancies in high-income countries each year."
            ]}
            variant="simple"
          />
        </div>
      </Section>

      {/* About Section */}
      <TeamSection title="Led by Healthcare and AI Experts" />

      {/* Investment Section */}
      <CTASection
        title="Join Us in Transforming Healthcare"
        description="We are raising £10 million to advance our technology, achieve regulatory milestones, and accelerate market adoption. Don't miss your opportunity to be part of the future of healthcare."
        buttonText="Request Investor Deck"
        buttonLink="/investors"
        buttonVariant="white"
      />

      {/* Contact Section */}
      <Section id="contact">
        <SectionTitle>Get in Touch</SectionTitle>
        
        <div className="max-w-4xl mx-auto">
          <Card shadow={false} className="bg-lightgray">
            <CardContent className="p-8 text-center">
              <h3 className="text-xl font-semibold mb-4">Interested in learning more?</h3>
              <p className="text-gray-700 mb-6">Contact us to discuss partnerships, investment opportunities, or to learn more about our technology.</p>
              <Button href="/contact" variant="primary">Contact Us</Button>
            </CardContent>
          </Card>
          <div className="text-center mt-8">
            <p className="text-gray-700">Or email us directly at: <a href="mailto:funding@senti-tech.uk" className="text-primary font-medium">funding@senti-tech.uk</a></p>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default HomePage;